import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';
import { RootState, ThunkServices } from '../../app/store';

// State
export interface ProductsState {
    availableProducts: {
        [key: string]: endpoints.ProductDTO;
    };
    loading: boolean;
}

export const initialState: ProductsState = {
    availableProducts: {},
    loading: false,
};

// Thunks
export const getProducts = createAsyncThunk<
    endpoints.ProductDTO[],
    void,
    {
        state: RootState;
        extra: ThunkServices
    }
>('products/getProducts',
    async (_, { dispatch, extra: { productService } }) => {
    const products = import.meta.env.VITE_DS_PRODUCTS;
    if (!products) {
        throw new Error('No productIds set');
    }
    const productIds = products.split(',');

    dispatch(setProductsLoading({loading: true}));
    try {
        const response = await productService.getProducts(productIds);
        dispatch(setProducts( {products: response} ));
        return response;
    } catch (error) {
        dispatch(setProductsLoading({loading: true}));
        throw error;
    }
});

export const productsSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {
        setProducts: (state, action: PayloadAction<{products: endpoints.ProductDTO[]}>) => {
            const productsById = keyBy(action.payload.products, (product) => product.productId);
            state.availableProducts = productsById;
        },
        setProductsLoading: (state, action: PayloadAction<{loading :boolean}>) => {
            if (action.payload.loading !== undefined) {
                state.loading = action.payload.loading;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProducts.pending, (state) => {
                state.loading = true;
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                const productsById = keyBy(action.payload, (product) => product.productId);
                state.availableProducts = productsById;
                state.loading = false;
            })
            .addCase(getProducts.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { setProducts, setProductsLoading } = productsSlice.actions;

export const productsReducer = productsSlice.reducer;